// authSlice.js - Redux Toolkit slice for auth
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAPI } from "./api";

// Auth state constants
export const AUTH_STATE = {
  INITIAL: "initial",
  LOADING: "loading",
  AUTHENTICATED: "authenticated",
  FAILED: "failed",
  FAILED_LICENSE: "failed_license",
};

// Initial state
const initialState = {
  email: "",
  password: "",
  accessCode: undefined,
  refreshCode: undefined,
  expirationDate: undefined,
  isRefreshing: false,
  isUserLoading: false,
  user: undefined,
  state: AUTH_STATE.INITIAL,
};

// Async thunks that use the API service
export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    const result = await authAPI.login(username, password);

    if (result.success) {
      const { access_token, refresh_token, expires_in } = result.data;
      const expDate = new Date().getTime() + expires_in * 1000;

      // After successful login, load user
      dispatch(loadUser(access_token));

      return {
        accessCode: access_token,
        refreshCode: refresh_token,
        expirationDate: expDate,
      };
    } else if (result.error === "license") {
      return rejectWithValue({ state: AUTH_STATE.FAILED_LICENSE });
    } else {
      return rejectWithValue({ state: AUTH_STATE.FAILED });
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const auth = getState().auth;

    if (!auth.refreshCode) {
      return rejectWithValue("No refresh token available");
    }

    const result = await authAPI.refreshToken(auth.refreshCode);

    if (result.success) {
      const { access_token, refresh_token, expires_in } = result.data;
      const expDate = new Date().getTime() + expires_in * 1000;

      // Load user with the new token
      dispatch(loadUser(access_token));

      return {
        accessCode: access_token,
        refreshCode: refresh_token,
        expirationDate: expDate,
      };
    } else {
      // Logout if refresh fails
      dispatch(logoutUser());
      return rejectWithValue("Refresh token failed");
    }
  }
);

export const loadUser = createAsyncThunk(
  "auth/loadUser",
  async (accessToken, { getState, dispatch, rejectWithValue }) => {
    // Use provided token or get from state
    const token = accessToken || getState().auth.accessCode;

    if (!token) {
      return rejectWithValue("No access token available");
    }

    const result = await authAPI.loadUser(token);

    if (result.success) {
      return result.data;
    } else {
      // If unauthorized or other critical error, logout
      if (result.status === 401) {
        dispatch(logoutUser());
      }
      return rejectWithValue("Failed to load user");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { getState, dispatch }) => {
    const token = getState().auth.accessCode;

    if (token) {
      await authAPI.logout(token);
    }

    // Always dispatch logout action regardless of API result
    dispatch(logout());
    return null;
  }
);

// Thunk for checking and refreshing expired tokens
export const refreshTokenIfNeeded = createAsyncThunk(
  "auth/refreshTokenIfNeeded",
  async (_, { getState, dispatch }) => {
    const auth = getState().auth;
    const currentTime = new Date().getTime();

    // Add a buffer (e.g., 30 seconds) to prevent edge cases
    const expirationBuffer = 30 * 1000;
    const isExpired = currentTime >= auth.expirationDate - expirationBuffer;

    if (auth.accessCode && isExpired && !auth.isRefreshing) {
      console.log(
        "[refreshTokenIfNeeded] Token expired or about to expire. Refreshing"
      );
      return dispatch(refreshToken());
    } else if (auth.accessCode && !auth.user && !auth.isUserLoading) {
      console.log("[refreshTokenIfNeeded] No user data, loading user");
      return dispatch(loadUser());
    }

    return null;
  }
);

// Create the auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setPassword: (state, action) => {
      state.password = action.payload;
    },
    logout: (_state, _action) => {
      return initialState;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isRefreshing = true;
        state.state = AUTH_STATE.LOADING;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.accessCode = action.payload.accessCode;
        state.refreshCode = action.payload.refreshCode;
        state.expirationDate = action.payload.expirationDate;
        state.state = AUTH_STATE.AUTHENTICATED;
        state.isRefreshing = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.accessCode = undefined;
        state.refreshCode = undefined;
        state.expirationDate = undefined;
        state.isRefreshing = false;
        state.state = action.payload?.state || AUTH_STATE.FAILED;
      })

      // Refresh token
      .addCase(refreshToken.pending, (state) => {
        state.isRefreshing = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.accessCode = action.payload.accessCode;
        state.refreshCode = action.payload.refreshCode;
        state.expirationDate = action.payload.expirationDate;
        state.isRefreshing = false;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isRefreshing = false;
        // The logout action will be dispatched separately
      })

      // Load user
      .addCase(loadUser.pending, (state) => {
        state.isUserLoading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isUserLoading = false;
      })
      .addCase(loadUser.rejected, (state) => {
        state.isUserLoading = false;
        // The logout action may be dispatched separately if needed
      });
  },
});

// Export actions from the slice
export const { setEmail, setPassword, logout, reset } = authSlice.actions;

// Export the reducer
export default authSlice.reducer;
